import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  distinctUntilChanged,
  exhaustMap,
  from,
  map,
  merge,
  of,
  switchMap,
  tap,
  EMPTY,
  pairwise,
  filter,
  withLatestFrom,
} from 'rxjs';
import { EStorageKey } from '@app/app.enums';
import { AuthActions } from './auth.actions';
import { selectAuth } from './auth.selectors';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { AuthApiService } from '@core/api/auth-api.service';
import {
  KeycloakSessionService,
  localReturnPath,
} from '@core/services/auth/keycloak-session.service';
import { ToastService } from 'ngx-mt-ui-lib/services/toast';
import { TranslateService } from '@ngx-translate/core';
import { isPublicRoutePath } from '@core/consts/public-access.const';

@Injectable()
export class AuthEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(AuthApiService);
  private readonly session = inject(KeycloakSessionService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);

  readonly sessionChanged$ = createEffect(() =>
    this.session.state$.pipe(
      map((session) => AuthActions.sessionChanged({ session })),
    ),
  );

  readonly sessionEnded$ = createEffect(
    () =>
      this.session.state$.pipe(
        pairwise(),
        filter(
          ([before, after]) =>
            before.authenticated &&
            !after.authenticated &&
            !this.session.ending &&
            !isPublicRoutePath(location.pathname),
        ),
        tap(() => this.router.navigate(['/auth-code'])),
      ),
    { dispatch: false },
  );

  readonly securityAdministrator$ = createEffect(
    () =>
      this.store.select(selectAuth).pipe(
        filter((state) => state.init),
        map((state) => state.authenticated && state.isSecurityAdministrator),
        distinctUntilChanged(),
        filter(() => !isPublicRoutePath(location.pathname)),
        tap((restricted) => {
          if (restricted) {
            if (
              ['/security-administrator', '/auth-code'].includes(
                location.pathname,
              )
            )
              return;
            const returnPath = `${location.pathname}${location.search}${location.hash}`;
            void this.router.navigate(['/security-administrator'], {
              replaceUrl: true,
              queryParams: { returnPath },
            });
          } else if (
            this.session.authenticated &&
            this.router.url.split(/[?#]/, 1)[0] === '/security-administrator'
          ) {
            const path = this.router.parseUrl(this.router.url).queryParams[
              'returnPath'
            ];
            void this.router.navigateByUrl(localReturnPath(path) ?? '/', {
              replaceUrl: true,
            });
          }
        }),
      ),
    { dispatch: false },
  );

  readonly currentUser$ = createEffect(() =>
    merge(
      this.session.state$.pipe(
        map(
          (session) =>
            session.authenticated && !session.isSecurityAdministrator,
        ),
        distinctUntilChanged(),
      ),
      this.actions$.pipe(
        ofType(AuthActions.requestCurrentUser),
        withLatestFrom(this.session.state$),
        map(
          ([, session]) =>
            session.authenticated && !session.isSecurityAdministrator,
        ),
      ),
    ).pipe(
      switchMap((authenticated) =>
        authenticated && !isPublicRoutePath(location.pathname)
          ? this.api.getCurrentUserInfo().pipe(
              map((user) =>
                user
                  ? AuthActions.requestCurrentUserSuccess({ user })
                  : AuthActions.requestCurrentUserError({
                      message: 'AUTH.PROFILE_ERROR',
                    }),
              ),
              catchError((error: unknown) =>
                of(
                  AuthActions.requestCurrentUserError({
                    message: 'AUTH.PROFILE_ERROR',
                    denied:
                      error instanceof HttpErrorResponse &&
                      error.status === 403,
                  }),
                ),
              ),
            )
          : of(AuthActions.requestCurrentUserSuccess({ user: null })),
      ),
    ),
  );

  readonly authorize$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.authorize),
        exhaustMap(({ returnPath }) =>
          from(this.session.login(returnPath)).pipe(
            catchError(() => {
              this.store.dispatch(
                AuthActions.sessionFailed({ message: 'AUTH.SESSION_ERROR' }),
              );
              return EMPTY;
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  readonly logout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logout),
        exhaustMap(({ returnPath }) =>
          from(this.session.logout(returnPath)).pipe(
            catchError(() => {
              this.store.dispatch(
                AuthActions.sessionFailed({ message: 'AUTH.SESSION_ERROR' }),
              );
              return EMPTY;
            }),
          ),
        ),
      ),
    { dispatch: false },
  );

  readonly favorite$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.setFavoriteUrl),
        tap(({ favoriteUrl }) =>
          localStorage.setItemJson(EStorageKey.MENU_FAVORITE, favoriteUrl),
        ),
      ),
    { dispatch: false },
  );

  readonly errors$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.sessionFailed, AuthActions.requestCurrentUserError),
        tap(({ message }) =>
          this.toast.add({
            level: 'error',
            summary: this.translate.instant(message),
          }),
        ),
      ),
    { dispatch: false },
  );
}
